import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCommunity } from "@/context/CommunityContext";
import TopBar from "@/components/TopBar";
import UserPicker from "@/components/UserPicker";

const EMPTY_SETS = [
  { a: "", b: "" },
  { a: "", b: "" },
  { a: "", b: "" },
];

export default function GameDetail() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { slug } = useCommunity();
  const [game, setGame] = useState(null);
  const [matches, setMatches] = useState([]);
  const [board, setBoard] = useState([]);
  const [myMatches, setMyMatches] = useState([]);
  const [opponent, setOpponent] = useState({ id: null, name: "" });
  const [sets, setSets] = useState(EMPTY_SETS);
  const [score, setScore] = useState({ a: "", b: "" });
  const [format, setFormat] = useState("Best of 3");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api
      .get(`/communities/${slug}/games`)
      .then(({ data }) => setGame((data || []).find((g) => g.id === gameId) || null))
      .catch(() => setGame(null));
  }, [slug, gameId]);

  const reload = () => {
    api.get(`/games/${gameId}/matches`).then(({ data }) => setMatches(data || [])).catch(() => setMatches([]));
    api.get(`/games/${gameId}/leaderboard`).then(({ data }) => setBoard(data || [])).catch(() => setBoard([]));
    if (user) api.get("/me/matches").then(({ data }) => setMyMatches((data || []).filter((m) => m.game_id === gameId))).catch(() => setMyMatches([]));
  };
  useEffect(reload, [gameId, user]);

  const startLoser = async () => {
    setError("");
    if (!opponent.id) {
      setError("Bitte wähle einen Gegner aus.");
      return;
    }
    try {
      const { data } = await api.post("/loser", { game_id: gameId, opponent_id: opponent.id });
      navigate(`/loser/${data.id}`);
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const sendMatch = async () => {
    setError("");
    if (!opponent.id) {
      setError("Bitte wähle einen Gegner aus.");
      return;
    }
    setBusy(true);
    try {
      const payload =
        game.game_type === "SET_MATCH"
          ? { game_id: gameId, opponent_id: opponent.id, match_format: format, sets: sets.filter((s) => s.a !== "" && s.b !== "") }
          : { game_id: gameId, opponent_id: opponent.id, score_a: score.a, score_b: score.b };
      const { data } = await api.post("/matches", payload);
      setSent(data);
      setSets(EMPTY_SETS);
      setScore({ a: "", b: "" });
      reload();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!game) return <div className="p-10 text-center text-sm text-[var(--ink-soft)]">wird geladen …</div>;

  const isLoser = game.game_type === "LETTER_GAME";
  const myLoserMatches = null;

  return (
    <div data-testid="game-detail-page">
      <TopBar back="/spielen" title={game.title} />
      {game.image ? (
        <div className="h-40 w-full bg-[var(--brand-soft)]">
          <img src={fileUrl(game.image)} alt="" className="h-full w-full object-cover" />
        </div>
      ) : null}
      <div className="mx-auto max-w-2xl px-5 py-7">
        <h1 className="text-4xl">{game.title}</h1>
        {game.rules_text ? <p className="mt-3 whitespace-pre-line text-base leading-relaxed">{game.rules_text}</p> : null}

        {!user ? (
          <div className="mt-7 rounded-2xl border bg-white p-5">
            <p className="text-sm">Für Matches brauchst du ein Konto.</p>
            <Link to="/login" className="btn btn-primary mt-3 !py-2.5 !text-xs" data-testid="game-login">ANMELDEN</Link>
          </div>
        ) : (
          <section className="mt-7 rounded-3xl border bg-white p-5" data-testid="match-form">
            <p className="eyebrow">{isLoser ? `${game.word || "LOSER"} starten` : "Neues Match"}</p>
            <div className="mt-3">
              <UserPicker value={opponent.id} onChange={(id, name) => setOpponent({ id, name })} label="Gegner auswählen" testId="match-opponent" />
            </div>

            {!isLoser && game.game_type === "SET_MATCH" ? (
              <div className="mt-5">
                <div className="eyebrow mb-1">Matchformat</div>
                <select className="field" value={format} onChange={(e) => setFormat(e.target.value)} data-testid="match-format">
                  <option>1 Satz</option>
                  <option>Best of 3</option>
                  <option>Eigenes Format</option>
                </select>
                <div className="mt-4 space-y-2">
                  {sets.map((set, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="w-16 text-xs text-[var(--ink-soft)]">Satz {index + 1}</span>
                      <input
                        className="field !w-20 text-center"
                        inputMode="numeric"
                        value={set.a}
                        onChange={(e) => setSets(sets.map((s, i) => (i === index ? { ...s, a: e.target.value } : s)))}
                        data-testid={`set-${index + 1}-a`}
                      />
                      <span className="text-sm">:</span>
                      <input
                        className="field !w-20 text-center"
                        inputMode="numeric"
                        value={set.b}
                        onChange={(e) => setSets(sets.map((s, i) => (i === index ? { ...s, b: e.target.value } : s)))}
                        data-testid={`set-${index + 1}-b`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {!isLoser && game.game_type === "SCORE_MATCH" ? (
              <div className="mt-5 flex items-center gap-2">
                <input className="field !w-24 text-center" inputMode="numeric" value={score.a} onChange={(e) => setScore({ ...score, a: e.target.value })} data-testid="score-a" />
                <span>:</span>
                <input className="field !w-24 text-center" inputMode="numeric" value={score.b} onChange={(e) => setScore({ ...score, b: e.target.value })} data-testid="score-b" />
              </div>
            ) : null}

            {error ? <p className="mt-3 text-sm text-red-700" data-testid="match-error">{error}</p> : null}
            {sent ? (
              <p className="mt-3 rounded-xl bg-[var(--brand-soft)] px-4 py-3 text-sm" data-testid="match-sent">
                Ergebnis an {sent.player_b_name} gesendet. Das Match zählt erst nach der Bestätigung.
              </p>
            ) : null}

            <button
              className="btn btn-primary mt-5 w-full"
              onClick={isLoser ? startLoser : sendMatch}
              disabled={busy}
              data-testid={isLoser ? "loser-start" : "match-send"}
            >
              {isLoser ? `${game.word || "LOSER"} STARTEN` : "ERGEBNIS SENDEN"}
            </button>
          </section>
        )}

        {!isLoser ? (
          <>
            <section className="mt-9" data-testid="my-matches">
              <p className="eyebrow">Meine letzten Matches</p>
              {myMatches.length === 0 ? (
                <p className="mt-3 text-sm text-[var(--ink-soft)]">Noch keine Matches.</p>
              ) : (
                <ul className="mt-3 divide-y overflow-hidden rounded-2xl border bg-white">
                  {myMatches.map((match) => (
                    <li key={match.id} className="px-4 py-3 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <span className="truncate">{match.player_a_name} vs {match.player_b_name}</span>
                        <span className="font-semibold">{match.score_a} : {match.score_b}</span>
                      </div>
                      <p className="mt-1 text-xs text-[var(--ink-soft)]">
                        {match.status === "CONFIRMED" ? "bestätigt" : match.status === "PENDING" ? "wartet auf Bestätigung" : "widersprochen"}
                        {(match.sets || []).length ? ` · ${match.sets.map((s) => `${s.a}:${s.b}`).join("  ")}` : ""}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="mt-9" data-testid="game-leaderboard">
              <p className="eyebrow">Rangliste</p>
              {board.length === 0 ? (
                <p className="mt-3 text-sm text-[var(--ink-soft)]">Noch keine bestätigten Matches.</p>
              ) : (
                <ol className="mt-3 divide-y overflow-hidden rounded-2xl border bg-white">
                  {board.map((row, i) => (
                    <li key={row.user_id} className="flex items-center gap-3 px-4 py-3 text-sm">
                      <span className="w-5 text-[var(--ink-soft)]">{i + 1}</span>
                      <span className="flex-1 truncate">{row.user_name}</span>
                      <span className="font-semibold">{row.wins} : {row.losses}</span>
                    </li>
                  ))}
                </ol>
              )}
            </section>

            <section className="mt-9" data-testid="all-matches">
              <p className="eyebrow">Letzte Ergebnisse</p>
              <ul className="mt-3 divide-y overflow-hidden rounded-2xl border bg-white">
                {matches.length === 0 ? (
                  <li className="px-4 py-3 text-sm text-[var(--ink-soft)]">Noch keine Ergebnisse.</li>
                ) : (
                  matches.slice(0, 10).map((match) => (
                    <li key={match.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                      <span className="truncate">{match.player_a_name} vs {match.player_b_name}</span>
                      <span className="font-semibold">{match.score_a} : {match.score_b}</span>
                    </li>
                  ))
                )}
              </ul>
            </section>
          </>
        ) : (
          <p className="mt-8 text-sm text-[var(--ink-soft)]">
            Offene und laufende Partien findest du in deinem Profil unter „Offene Bestätigungen“ und „Meine Matches“.
          </p>
        )}
      </div>
    </div>
  );
}
